import { useEffect, useState } from 'react'
import { FormProvider, useForm, type FieldErrors, type Resolver } from 'react-hook-form'
import { Box, useMediaQuery } from '@mui/material'

import { RowBox } from '@components/shared/RowBox'
import { Input } from '@components/shared/Input'
import { EditIcon } from '@assets/svgs/EditIcon'
import { emotionTheme } from '@styles/theme'
import { initialFormValues } from './form/initialFormValues'
import { ReturnModal } from '@components/modals/ReturnModal'
import { WarningIcon } from '@assets/svgs/WarningIcon'
import { Textarea } from '@components/shared/Textarea'
import { PlusCircleIcon } from '@assets/svgs/PlusCircleIcon'
import { BaseModal2 } from '@components/modals/BaseModal2'
import { Select } from '@components/shared/Select'
import { addEditAdditionalDepreciationSchema } from './form/validations'
import { useDepreciation } from '@pages/Depreciation/context'
import { unMaskCurrency } from '@utils/masks/unMaskCurrency'
import { useUser } from '@hooks/useUser'

import type { AddEditAdditionalDepreciationFormProps } from './form/types'
import type { AddEditAdditionalDepreciationModalProps } from './types'

import * as S from '@pages/OtherExpenses/components/modals/AddEditAdditionalExpenses/styles'

export const AddEditAdditionalDepreciation = ({
  data,
  open,
  onClose,
  canEdit = true,
  ...rest
}: AddEditAdditionalDepreciationModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)

  const { filiData } = useUser()

  const {
    additionalDepreciationData,
    additionalDepreciationAccounts,
    isAdditionalDepreciationAccountsLoading,
    isAddEditAdditionalDepreciationLoading,
    saveAdditionalDepreciation
  } = useDepreciation()

  const methods = useForm<AddEditAdditionalDepreciationFormProps>({
    defaultValues: initialFormValues,
    resolver: addEditAdditionalDepreciationSchema as unknown as Resolver<AddEditAdditionalDepreciationFormProps>
  })

  const [openCancelModal, setOpenCancelModal] = useState(false)
  const [duplicatedFormData, setDuplicatedFormData] = useState<AddEditAdditionalDepreciationFormProps | null>(null)

  const isEdit = !!data

  const accountDescriptionOptions = additionalDepreciationAccounts.map((option) => ({
    label: option.description,
    value: option.accountingCode
  }))

  const save = (formData: AddEditAdditionalDepreciationFormProps) =>
    saveAdditionalDepreciation(formData).then((success) => success && onClose(false))

  const onError = (errors: FieldErrors<AddEditAdditionalDepreciationFormProps>) => console.log({ errors })

  const onSubmit = (formData: AddEditAdditionalDepreciationFormProps) => {
    if (!canEdit) return

    formData.id = data?.id ?? null
    formData.filiCode = filiData?.filiCode!
    formData.competenceDate = filiData?.startPeriod!

    // RN04: conta repetida é permitida, mas pede confirmação
    const isDuplicated = additionalDepreciationData?.items?.some(
      (item) => item.accountingCode === formData.accountNumber && item.id !== formData.id
    )

    if (isDuplicated) return setDuplicatedFormData(formData)

    save(formData)
  }

  useEffect(() => {
    !!data &&
      methods.reset({
        accountDescription: data.accountingCode || '',
        accountNumber: data.accountingCode || '',
        accountDescriptionText: data.accountingDescription || '',
        additionalDepreciationValue: data.value?.toFixed(2) || '',
        observations: data.observations || ''
      })
  }, [data, open])

  useEffect(() => {
    !open && methods.reset(initialFormValues)
  }, [open])

  return (
    <>
      <BaseModal2
        open={open}
        width={isMobile ? 'calc(100% - 20px)' : 870}
        heightContainer={isMobile ? 'calc(100% - 20px)' : 'auto '}
        title={isEdit ? 'Editar Depreciação' : 'Adicionar Depreciação'}
        headerIcon={isEdit ? <EditIcon /> : <PlusCircleIcon />}
        onClose={() => setOpenCancelModal(true)}
        footer={{
          cancelButtonText: 'Cancelar',
          cancelButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
          confirmButtonText: canEdit ? 'Salvar' : undefined,
          confirmButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
          onConfirm: canEdit ? () => methods.handleSubmit(onSubmit, onError)() : undefined,
          loading: isAddEditAdditionalDepreciationLoading
        }}
        {...rest}
      >
        <S.Title>
          {!isEdit
            ? 'Preencha todos os campos para adicionar uma nova depreciação.'
            : 'Preencha todos os campos antes de confirmar a edição da depreciação.'}
        </S.Title>

        <FormProvider {...methods}>
          <S.Form>
            <RowBox columns={4}>
              <Box sx={{ gridColumn: 'span 2' }}>
                <Select
                  required
                  name="accountDescription"
                  label="Descrição Conta"
                  placeholder="Selecione a desc. da conta"
                  options={accountDescriptionOptions}
                  onChange={({ target }) => {
                    const accountDescriptionText = accountDescriptionOptions.find(
                      (option) => option.value === target.value
                    )?.label

                    methods.setValue('accountDescriptionText', accountDescriptionText as string)
                    methods.setValue('accountDescription', target.value as string)
                    methods.setValue('accountNumber', target.value as string, { shouldValidate: true })
                  }}
                  loading={isAdditionalDepreciationAccountsLoading}
                  disabled={!canEdit || isAddEditAdditionalDepreciationLoading}
                />
              </Box>
              <Input name="accountNumber" label="Conta" placeholder="Nº da conta" required disabled />
              <Input
                required
                name="additionalDepreciationValue"
                label="Valor Depreciação Adic."
                placeholder="R$ 0,00"
                maskType="currency"
                maxDigits={8}
                onBlur={({ target }) => {
                  methods.setValue('additionalDepreciationValue', unMaskCurrency(target.value).toFixed(2))
                }}
                disabled={!canEdit || isAddEditAdditionalDepreciationLoading}
              />
            </RowBox>

            <RowBox>
              <Textarea
                required
                name="observations"
                label="Observações"
                placeholder="Descreva as suas observações"
                disabled={!canEdit || isAddEditAdditionalDepreciationLoading}
              />
            </RowBox>
          </S.Form>
        </FormProvider>
      </BaseModal2>

      <ReturnModal
        open={openCancelModal}
        headerIcon={<WarningIcon />}
        title="Deseja realmente cancelar a ação?"
        message="Todo o seu progresso será perdido!"
        headerTitle="Você tem certeza?"
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenCancelModal}
        onConfirm={() => {
          onClose(false)
          setOpenCancelModal(false)
        }}
      />

      <ReturnModal
        open={!!duplicatedFormData}
        headerIcon={<WarningIcon />}
        headerTitle="Conta já lançada"
        title="Já existe uma depreciação adicional para esta conta."
        message="Deseja incluir mesmo assim?"
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        isLoading={isAddEditAdditionalDepreciationLoading}
        onClose={() => setDuplicatedFormData(null)}
        onConfirm={() => save(duplicatedFormData!).then(() => setDuplicatedFormData(null))}
      />
    </>
  )
}
